import {
  formatMinor,
  fractionDigitsFor,
  newCommandId,
  outsideShiftMethods,
  outsideShiftRefundProblem,
  parseMoneyToMinor,
} from '@didto/domain';
import { Button, Field, Input, KvRow, Notice, Select, Sheet } from '@didto/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ReauthForm } from '@/app/ownerReview';
import { useSession } from '@/app/session';
import { useToast } from '@/app/toast';
import { runCommand } from '@/lib/api';
import { toAppError } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import type { Order } from '@/lib/orders';
import { orderKeys } from '@/lib/orders';
import type { PaymentMethod } from '@/lib/payments';
import { paymentKeys } from '@/lib/payments';

/**
 * ADR-033: the owner gives money back by GCash or bank transfer from the signed-in account, outside any shift. Money
 * only (no items), never cash: cash goes back at a drawer, on the POS tablet in a shift. The server takes it with an
 * authenticator verified and a recent authentication; when the authentication is older, this asks for the code and
 * sends the same command again. It needs the server now, so it waits for the connection (ADR-037).
 */
export function OutsideShiftRefundSheet({
  order,
  refundableMinor,
  methods,
  currency,
  onClose,
}: {
  order: Order | null;
  refundableMinor: number;
  methods: PaymentMethod[];
  currency: string;
  onClose: () => void;
}) {
  return (
    <Sheet open={!!order} onClose={onClose} title="Refund by GCash or transfer">
      {order && (
        <OutsideShiftRefundForm
          key={order.id}
          order={order}
          refundableMinor={refundableMinor}
          methods={methods}
          currency={currency}
          onClose={onClose}
        />
      )}
    </Sheet>
  );
}

function OutsideShiftRefundForm({
  order,
  refundableMinor,
  methods,
  currency,
  onClose,
}: {
  order: Order;
  refundableMinor: number;
  methods: PaymentMethod[];
  currency: string;
  onClose: () => void;
}) {
  const s = useSession();
  const qc = useQueryClient();
  const { toast } = useToast();
  const online = useOnline();
  const offered = outsideShiftMethods(methods);
  const [methodId, setMethodId] = useState(offered[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reauth, setReauth] = useState(false);
  // one command id for this refund: sent again after a fresh verification, never applied twice
  const [commandId] = useState(newCommandId);

  if (s.me?.mfa_enrolled !== true)
    return (
      <div className="d-stack" data-testid="outside-refund-mfa-setup">
        <Notice tone="info">
          Giving money back from your phone needs an authenticator: a password alone never does.
        </Notice>
        <Link to="/settings/security" className="d-btn d-btn--primary">
          Set up an authenticator
        </Link>
      </div>
    );

  const method = offered.find((m) => m.id === methodId) ?? null;
  const amountMinor =
    parseMoneyToMinor(amount, { fractionDigits: fractionDigitsFor(currency) }) ?? 0;
  const problem = outsideShiftRefundProblem({
    amountMinor,
    refundableMinor,
    method,
    reference,
    reason,
  });

  const send = async () => {
    if (problem || !method) return;
    setBusy(true);
    setError(null);
    try {
      await runCommand(
        'order.refund_outside_shift',
        {
          order_id: order.id,
          version: order.version,
          amount_minor: amountMinor,
          payment_method_id: method.id,
          reason: reason.trim(),
          ...(reference.trim() ? { reference: reference.trim() } : {}),
        },
        { commandId, locationId: order.location_id },
      );
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['orders'] }),
        qc.invalidateQueries({ queryKey: orderKeys.detail(order.id) }),
        qc.invalidateQueries({ queryKey: paymentKeys.money(order.id) }),
      ]);
      toast(`Gave back ${formatMinor(amountMinor, currency)} by ${method.name}`, 'success');
      onClose();
    } catch (e) {
      const err = toAppError(e);
      if (err.code === 'reauth_required') setReauth(true);
      else setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (reauth)
    return (
      <ReauthForm
        reason="action"
        note="Giving money back from your phone asks for a fresh verification."
        onVerified={() => {
          setReauth(false);
          void send();
        }}
        onCancel={() => setReauth(false)}
      />
    );

  return (
    <div className="d-stack" data-testid="outside-refund-sheet">
      <p className="d-muted">
        Money you send yourself, outside any shift: it does not touch a drawer.
      </p>
      <div className="d-kv">
        <KvRow label="Order" value={<span className="d-mono">{order.order_number}</span>} />
        <KvRow
          label="Up to"
          value={<span className="d-mono">{formatMinor(refundableMinor, currency)}</span>}
        />
      </div>
      {offered.length === 0 ? (
        <Notice tone="danger" role="alert">
          No method to give money back by: enable GCash or a bank transfer method under Payment
          methods.
        </Notice>
      ) : (
        <>
          <Field label="Amount">
            {(id) => (
              <Input
                id={id}
                inputMode="decimal"
                className="d-input--money"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="outside-refund-amount"
              />
            )}
          </Field>
          <Field label="Sent by">
            {(id) => (
              <Select
                id={id}
                value={methodId}
                onChange={(e) => setMethodId(e.target.value)}
                data-testid="outside-refund-method"
              >
                {offered.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label={
              method?.requires_reference ? 'Transfer reference' : 'Transfer reference (optional)'
            }
            help="The reference the transfer shows in the app it was sent from."
          >
            {(id) => (
              <Input
                id={id}
                value={reference}
                maxLength={64}
                onChange={(e) => setReference(e.target.value)}
                data-testid="outside-refund-reference"
              />
            )}
          </Field>
          <Field label="Reason">
            {(id) => (
              <Input
                id={id}
                value={reason}
                maxLength={200}
                onChange={(e) => setReason(e.target.value)}
                data-testid="outside-refund-reason"
              />
            )}
          </Field>
          {(error ?? (amount !== '' && problem)) && (
            <div className="d-error" role="alert" data-testid="outside-refund-error">
              {error ?? problem}
            </div>
          )}
          <Button
            variant="primary"
            size="lg"
            block
            busy={busy}
            disabled={!online || !!problem || busy}
            onClick={() => void send()}
            data-testid="outside-refund-confirm"
          >
            Give back {amountMinor > 0 ? formatMinor(amountMinor, currency) : ''}
          </Button>
        </>
      )}
    </div>
  );
}
